import {
  StringHelpers,
  DayOfWeek,
  getEnumValues,
  WithRef,
} from '@principle-theorem/shared';
import {
  IAIReceptionistKnowledgeBase,
  IAIReceptionistCustomSection,
  IAIReceptionistCustomQA,
  IPractice,
  IKnowledgeBaseBusinessHours,
  IKnowledgeBaseCustomSection,
  IKnowledgeBaseDayHours,
  IKnowledgeBasePushBody,
  IKnowledgeBaseQaPair,
  IKnowledgeBaseService,
  IKnowledgeBaseTreatmentPricingItem,
  IKnowledgeBaseEmergencyHours,
  IKnowledgeBaseFieldOverride,
  KNOWLEDGE_BASE_MAX_TEXT_LENGTH,
  KNOWLEDGE_BASE_MAX_AFTER_HOURS_MESSAGE_LENGTH,
  KNOWLEDGE_BASE_MAX_PUBLIC_HOLIDAY_NOTE_LENGTH,
  KNOWLEDGE_BASE_MIN_WINDOW_HOURS,
  KNOWLEDGE_BASE_MAX_WINDOW_HOURS,
  KNOWLEDGE_BASE_MAX_QA_QUESTION_LENGTH,
  KNOWLEDGE_BASE_MAX_QA_ANSWER_LENGTH,
  KNOWLEDGE_BASE_MAX_TREATMENT_NAME_LENGTH,
  KNOWLEDGE_BASE_MAX_TREATMENT_NOTES_LENGTH,
  KNOWLEDGE_BASE_MAX_PRICE_RANGE_LENGTH,
  KNOWLEDGE_BASE_MAX_COLLECTION_ITEMS,
} from '@principle-theorem/principle-core/interfaces';
import { Practice } from '../practice/practice';
import { isString } from 'lodash';
import {
  deriveKnowledgeBaseFromTemplates,
  IKnowledgeBaseTemplateDerivation,
} from './knowledge-base-template-derivation';
import { KnowledgeBaseFeesPayments } from './knowledge-base-fees-payments';
import { getExcludedTemplateIds } from './knowledge-base-excluded-templates';

const SECTION_QA_KEYS = [
  'generalServices',
  'cosmeticServices',
  'appointments',
  'feesPayments',
  'parkingAccess',
  'ourTeam',
] as const;

const PRICE_ON_ENQUIRY = 'Price on enquiry';

export class AIReceptionistKnowledgeBaseCompose {
  static async composeKnowledgeBase(
    practice: WithRef<IPractice>,
    knowledgeBase: IAIReceptionistKnowledgeBase
  ): Promise<IKnowledgeBasePushBody> {
    const brandRef = Practice.brandDoc(practice);

    const [derivation, feesPayments] = await Promise.all([
      deriveKnowledgeBaseFromTemplates({
        practice,
        brandRef,
        excludedTemplateIds: getExcludedTemplateIds(knowledgeBase),
      }),
      KnowledgeBaseFeesPayments.deriveFeesPayments(brandRef),
    ]);

    const sectionQaPairs = this._buildSectionQaPairs(knowledgeBase);
    const customSections = this._buildCustomSections(knowledgeBase);

    return {
      clinicName: practice.name,
      address: practice.address,
      phoneNumber: practice.phone,
      email: practice.email,
      website: practice.websiteUrl,
      businessHours: this._buildBusinessHours(practice),
      services: this._buildServices(derivation),
      specializations: [],
      treatmentPricing: this._buildTreatmentPricing(knowledgeBase, derivation),
      emergencyContact: StringHelpers.clampText(
        knowledgeBase.emergencyContact,
        KNOWLEDGE_BASE_MAX_TEXT_LENGTH
      ),
      emergencyHours: this._buildEmergencyHours(knowledgeBase.emergencyHours),
      publicHolidayNote: StringHelpers.clampText(
        knowledgeBase.publicHolidayNote ?? '',
        KNOWLEDGE_BASE_MAX_PUBLIC_HOLIDAY_NOTE_LENGTH
      ),
      afterHoursMessage: StringHelpers.clampText(
        knowledgeBase.afterHoursMessage ?? '',
        KNOWLEDGE_BASE_MAX_AFTER_HOURS_MESSAGE_LENGTH
      ),
      practiceDescription: StringHelpers.clampText(
        knowledgeBase.practiceDescription ?? '',
        KNOWLEDGE_BASE_MAX_TEXT_LENGTH
      ),
      generalServices: StringHelpers.clampText(
        derivation.generalServices,
        KNOWLEDGE_BASE_MAX_TEXT_LENGTH
      ),
      cosmeticServices: StringHelpers.clampText(
        derivation.cosmeticServices,
        KNOWLEDGE_BASE_MAX_TEXT_LENGTH
      ),
      appointmentsBooking: StringHelpers.clampText(
        knowledgeBase.appointmentsBooking ?? '',
        KNOWLEDGE_BASE_MAX_TEXT_LENGTH
      ),
      feesPayments: StringHelpers.clampText(
        feesPayments,
        KNOWLEDGE_BASE_MAX_TEXT_LENGTH
      ),
      parkingAccess: StringHelpers.clampText(
        knowledgeBase.parkingAccess ?? '',
        KNOWLEDGE_BASE_MAX_TEXT_LENGTH
      ),
      additionalInfo: StringHelpers.clampText(
        knowledgeBase.additionalInfo ?? '',
        KNOWLEDGE_BASE_MAX_TEXT_LENGTH
      ),
      ourTeam: StringHelpers.clampText(
        knowledgeBase.ourTeam ?? '',
        KNOWLEDGE_BASE_MAX_TEXT_LENGTH
      ),
      ...(customSections ? { customSections } : {}),
      ...(sectionQaPairs ? { sectionQaPairs } : {}),
      cancellationPolicy: StringHelpers.clampText(
        knowledgeBase.cancellationPolicy,
        KNOWLEDGE_BASE_MAX_TEXT_LENGTH
      ),
      allowsCancellations: knowledgeBase.allowsCancellations ?? false,
      cancellationFeeWindowHours: this._clampWindowHours(
        knowledgeBase.cancellationFeeWindowHours
      ),
      allowsRescheduling: knowledgeBase.allowsRescheduling ?? false,
      reschedulingPolicy: StringHelpers.clampText(
        knowledgeBase.reschedulingPolicy,
        KNOWLEDGE_BASE_MAX_TEXT_LENGTH
      ),
      reschedulingWindowHours: this._clampWindowHours(
        knowledgeBase.reschedulingWindowHours
      ),
    };
  }

  private static _buildServices(
    derivation: IKnowledgeBaseTemplateDerivation
  ): IKnowledgeBaseService[] {
    return derivation.items
      .slice(0, KNOWLEDGE_BASE_MAX_COLLECTION_ITEMS)
      .map((item) => {
        const description = item.description
          ? StringHelpers.clampText(
              item.description,
              KNOWLEDGE_BASE_MAX_TEXT_LENGTH
            )
          : undefined;
        return {
          name:
            StringHelpers.clampText(
              item.name,
              KNOWLEDGE_BASE_MAX_TREATMENT_NAME_LENGTH
            ) ?? '',
          ...(description ? { description } : {}),
        };
      });
  }

  private static _buildTreatmentPricing(
    knowledgeBase: IAIReceptionistKnowledgeBase,
    derivation: IKnowledgeBaseTemplateDerivation
  ): IKnowledgeBaseTreatmentPricingItem[] {
    return derivation.items
      .slice(0, KNOWLEDGE_BASE_MAX_COLLECTION_ITEMS)
      .map((item) => {
        const override = knowledgeBase.templateOverrides?.[item.templateId];
        const overriddenValue = override?.priceRange?.overridden
          ? override.priceRange.value
          : '';
        const priceRange =
          overriddenValue.trim().length > 0
            ? overriddenValue
            : PRICE_ON_ENQUIRY;
        const notes = this._buildTreatmentNotes(override?.notes);
        return {
          id: item.templateId,
          treatment:
            StringHelpers.clampText(
              item.name,
              KNOWLEDGE_BASE_MAX_TREATMENT_NAME_LENGTH
            ) ?? '',
          priceRange:
            StringHelpers.clampText(
              priceRange,
              KNOWLEDGE_BASE_MAX_PRICE_RANGE_LENGTH
            ) ?? '',
          ...(notes ? { notes } : {}),
        };
      });
  }

  /**
   * `KbTreatmentPricingItem.notes` must be non-empty when present, so a blank
   * or unticked override omits the key rather than sending an empty string.
   */
  private static _buildTreatmentNotes(
    override: IKnowledgeBaseFieldOverride | undefined
  ): string | undefined {
    if (!override?.overridden || override.value.trim().length === 0) {
      return undefined;
    }

    return StringHelpers.clampText(
      override.value,
      KNOWLEDGE_BASE_MAX_TREATMENT_NOTES_LENGTH
    );
  }

  private static _buildCustomSections(
    knowledgeBase: IAIReceptionistKnowledgeBase
  ): IKnowledgeBaseCustomSection[] | undefined {
    if (!knowledgeBase.customSections?.length) {
      return undefined;
    }

    return knowledgeBase.customSections
      .slice(0, KNOWLEDGE_BASE_MAX_COLLECTION_ITEMS)
      .map((section: IAIReceptionistCustomSection) => ({
        title:
          StringHelpers.clampText(
            section.title,
            KNOWLEDGE_BASE_MAX_TEXT_LENGTH
          ) ?? '',
        content:
          StringHelpers.clampText(
            section.content,
            KNOWLEDGE_BASE_MAX_TEXT_LENGTH
          ) ?? '',
        qa: this._buildQaPairs(section.qa),
      }));
  }

  /**
   * `KbQaPair` requires `q`/`a` to be non-empty and within 120/400, and caps each
   * collection at 100. A single breach rejects the WHOLE body with
   * `400 invalid_payload`, so offending pairs are clamped and dropped here.
   */
  private static _buildQaPairs(
    qaPairs: IAIReceptionistCustomQA[] | undefined
  ): IKnowledgeBaseQaPair[] {
    return (qaPairs ?? [])
      .map((qaPair: IAIReceptionistCustomQA) => ({
        id: qaPair.id,
        q:
          StringHelpers.clampText(
            qaPair.question,
            KNOWLEDGE_BASE_MAX_QA_QUESTION_LENGTH
          ) ?? '',
        a:
          StringHelpers.clampText(
            qaPair.answer,
            KNOWLEDGE_BASE_MAX_QA_ANSWER_LENGTH
          ) ?? '',
      }))
      .filter((qaPair) => qaPair.q.trim() !== '' && qaPair.a.trim() !== '')
      .slice(0, KNOWLEDGE_BASE_MAX_COLLECTION_ITEMS);
  }

  private static _buildSectionQaPairs(
    knowledgeBase: IAIReceptionistKnowledgeBase
  ): Record<string, IKnowledgeBaseQaPair[]> | undefined {
    const qaPairs = knowledgeBase.sectionQaPairs;
    if (!qaPairs) {
      return undefined;
    }

    const result: Record<string, IKnowledgeBaseQaPair[]> = {};

    for (const key of SECTION_QA_KEYS) {
      const items = this._buildQaPairs(qaPairs[key]);
      if (items.length > 0) {
        result[key] = items;
      }
    }

    return Object.keys(result).length > 0 ? result : undefined;
  }

  private static _buildBusinessHours(
    practice: WithRef<IPractice>
  ): IKnowledgeBaseBusinessHours {
    const days: Record<string, IKnowledgeBaseDayHours> = {};

    for (const dayKey of getEnumValues(DayOfWeek)) {
      days[dayKey] = { open: '', close: '', isClosed: true };
    }

    const roster = practice.schedule;

    if (!roster?.days) {
      return days as IKnowledgeBaseBusinessHours;
    }

    for (const rosterDay of roster.days) {
      const dayKey = rosterDay.dayOfWeek;
      if (dayKey in days) {
        days[dayKey] = {
          open: rosterDay.shift.from,
          close: rosterDay.shift.to,
          isClosed: false,
        };
      }
    }

    return days as IKnowledgeBaseBusinessHours;
  }

  private static _buildEmergencyHours(
    emergencyHoursValue: IAIReceptionistKnowledgeBase['emergencyHours']
  ): IKnowledgeBaseEmergencyHours | undefined {
    if (!emergencyHoursValue) {
      return undefined;
    }

    return isString(emergencyHoursValue)
      ? { available: true, hours: emergencyHoursValue }
      : {
          available: emergencyHoursValue.available,
          contact: emergencyHoursValue.contact,
          hours: emergencyHoursValue.hours,
        };
  }

  private static _clampWindowHours(
    value: number | undefined
  ): number | undefined {
    if (value === undefined || value < KNOWLEDGE_BASE_MIN_WINDOW_HOURS) {
      return undefined;
    }
    return Math.min(value, KNOWLEDGE_BASE_MAX_WINDOW_HOURS);
  }
}
